import { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { CalendarDays, CakeSlice, BookOpen, BookMarked, ShoppingCart, Users, ChevronLeft, ChevronRight, Refrigerator, LogOut, Bell, AlertTriangle, Clock, BarChart3, ListChecks, Ship, Camera, Anchor, Check, MessageCircle, Activity, Home, LayoutGrid, Settings, UserCog, Repeat, Calendar, Menu, X, Sun, Moon, Monitor, MessageSquarePlus, CloudDownload } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useAuth } from "@/lib/AuthContext";
import { useTheme } from "@/lib/ThemeContext";
import { api } from "@/lib/api";
import ExpiryNotifier from "@/components/ExpiryNotifier";
import NotificationBell from "@/components/NotificationBell";
import OfflineBanner from "@/components/OfflineBanner";
import OfflineDataCard from "@/components/OfflineDataCard";

const boatNavItems = [
  { to: "/", icon: CalendarDays, label: "Turnusplan", shortLabel: "Plan", page: "plan" },
  { to: "/baking", icon: CakeSlice, label: "Baking", shortLabel: "Baking", page: "baking" },
  { to: "/recipes", icon: BookOpen, label: "Oppskrifter", shortLabel: "Oppskrifter", page: "recipes" },
  { to: "/pantry", icon: Refrigerator, label: "Beholdning", shortLabel: "Lager", page: "pantry" },
  { to: "/shopping", icon: ShoppingCart, label: "Handleliste", shortLabel: "Handle", page: "shopping" },
  { to: "/my-lists", icon: ListChecks, label: "Mine Lister", shortLabel: "Lister", page: "my-lists" },
  { to: "/scan", icon: Camera, label: "Skann kvittering", shortLabel: "Skann", page: "scan" },
  { to: "/trips", icon: Ship, label: "Turer", shortLabel: "Turer", page: "trips" },
  { to: "/messages", icon: MessageCircle, label: "Meldinger", shortLabel: "Chat" },
  { to: "/waste", icon: BarChart3, label: "Matsvinn", shortLabel: "Svinn", page: "waste" },
  { to: "/statistikk", icon: BarChart3, label: "Vareforbruk", shortLabel: "Stats", page: "statistikk" },
  { to: "/boats", icon: Anchor, label: "Båter", shortLabel: "Båter", page: "boats" },
  { to: "/admin/health", icon: Activity, label: "Drift & helse", shortLabel: "Drift", adminOnly: true },
  { to: "/admin/overview", icon: LayoutGrid, label: "Admin-oversikt", shortLabel: "Oversikt", adminOnly: true },
  { to: "/admin/users", icon: UserCog, label: "Admin – Brukere", shortLabel: "Brukere", adminOnly: true },
  { to: "/admin/feedback", icon: MessageSquarePlus, label: "Tilbakemeldingar", shortLabel: "Feedback", adminOnly: true },
  { to: "/admin/home", icon: Settings, label: "Admin Home", shortLabel: "AdminHome", adminOnly: true },
];

// Home Edition — strict minimum: Hjem (budsjett), Oppskrifter, Beholdning, Handleliste
// Everything boat-related (skift, turer, meldinger, chat, båter, båt-admin) is gone.
// Husstand-admin er kun synlig for admin-brukere (for å kunne legge til medlemmer).
const homeNavItems = [
  { to: "/home", icon: Home, label: "Hjem", shortLabel: "Hjem" },
  { to: "/calendar", icon: Calendar, label: "Kalender", shortLabel: "Kalender" },
  { to: "/recipes", icon: BookOpen, label: "Oppskrifter", shortLabel: "Oppskrifter", page: "recipes" },
  { to: "/pantry", icon: Refrigerator, label: "Beholdning", shortLabel: "Lager", page: "pantry" },
  { to: "/shopping", icon: ShoppingCart, label: "Handleliste", shortLabel: "Handle", page: "shopping" },
  { to: "/boats", icon: Users, label: "Husstand", shortLabel: "Hus", page: "boats", adminOnly: true },
  { to: "/admin/users", icon: UserCog, label: "Brukere", shortLabel: "Brukere", adminOnly: true },
  { to: "/admin/home", icon: Settings, label: "Admin Home", shortLabel: "Admin", adminOnly: true },
];

export default function Layout({ servings, setServings }) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, activeBoat: ctxActiveBoat } = useAuth();
  const { theme, resolved, setTheme } = useTheme();
  const [notifications, setNotifications] = useState({ expired: [], expiring: [], total: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const [activeTrip, setActiveTrip] = useState(null);
  const [activeBoat, setActiveBoat] = useState(null);
  const [boats, setBoats] = useState([]);
  const [boatMenuOpen, setBoatMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Prefer live context activeBoat (includes my_access); fallback to local state
  const effectiveBoat = ctxActiveBoat || activeBoat;
  const myAccess = effectiveBoat?.my_access || {};
  const isHousehold = effectiveBoat?.kind === "household";
  const navItems = isHousehold ? homeNavItems : boatNavItems;
  const visibleNavItems = navItems.filter((item) => {
    if (item.adminOnly && user?.role !== "admin") return false;
    if (item.page && myAccess[item.page] === "none") return false;
    return true;
  });

  // Dual-edition users can swap sides from the header. Admins always have both.
  const editionsAllowed = user?.editions_allowed || (user?.role === "admin" ? ["boat", "household"] : []);
  const canSwitchEdition = editionsAllowed.length > 1;

  const switchEdition = async () => {
    const target = isHousehold ? "boat" : "household";
    if (!editionsAllowed.includes(target) && user?.role !== "admin") return;
    try {
      await api.setPreferredEdition(target);
      // Reset local storage + cookie so AuthContext picks the new side on reload
      localStorage.setItem("preferred_edition", target);
      // Full reload is the cleanest way to re-bootstrap activeBoat + nav
      window.location.href = target === "household" ? "/home" : "/";
    } catch (e) {
      console.error("Kunne ikke bytte edition:", e);
    }
  };

  const loadNotifications = useCallback(async () => {
    try {
      const res = await api.getExpiringItems();
      setNotifications(res.data);
    } catch {}
  }, []);

  const loadActiveTrip = useCallback(async () => {
    try {
      const res = await api.getActiveTrip();
      setActiveTrip(res.data);
    } catch {}
  }, []);

  const loadBoats = useCallback(async () => {
    try {
      const [boatsRes, activeRes] = await Promise.all([api.getBoats(), api.getActiveBoat()]);
      setBoats(boatsRes.data);
      setActiveBoat(activeRes.data);
    } catch {}
  }, []);

  // Memoise the filtered boats list so we don't re-filter on every render.
  // (Used in both desktop popover and mobile drawer — 4 references total.)
  const boatBoats = useMemo(
    () => (boats || []).filter((b) => (b.kind || "boat") === "boat"),
    [boats],
  );

  const switchBoat = async (boatId) => {
    try {
      await api.activateBoat(boatId);
      setBoatMenuOpen(false);
      loadBoats();
      loadActiveTrip();
      window.location.reload();
    } catch {}
  };

  useEffect(() => {
    if (user) { loadNotifications(); loadActiveTrip(); loadBoats(); const iv = setInterval(() => { loadNotifications(); loadActiveTrip(); }, 60000); return () => clearInterval(iv); }
  }, [user, loadNotifications, loadActiveTrip, loadBoats]);

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-stone-50" data-testid="app-layout">
      <OfflineBanner />
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex ${collapsed ? "w-16" : "w-56"} flex-shrink-0 bg-white border-r border-stone-200 flex-col transition-all duration-300 ease-in-out print-hide`}
        data-testid="sidebar"
      >
        <div className={`h-16 flex items-center border-b border-stone-200 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          {!collapsed && (
            <div className="flex items-center">
              <img
                src={isHousehold ? "https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/urzc6qg7_7540BF21-4413-4E40-BF72-4D12C742E009.png" : "https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/n0xjz0mz_E3443421-DE5D-4719-8957-3EE6E82E98C2.png"}
                alt="PantryBuddy"
                className={`w-auto rounded-lg ${isHousehold ? "h-14" : "h-12"}`}
                data-testid="sidebar-logo"
              />
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`${collapsed ? "" : "ml-auto"} p-1 rounded-md hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors`}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `group flex items-center rounded-lg ${collapsed ? "justify-center px-2" : "px-3"} py-2.5 text-sm font-medium transition-colors gap-3 ${
                  isActive ? "bg-stone-100 text-navy-800" : "text-stone-500 hover:bg-stone-50 hover:text-stone-800"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-40 flex h-14 md:h-16 items-center justify-between gap-x-4 border-b border-stone-200 bg-white/80 backdrop-blur-xl px-4 md:px-6" data-testid="app-header">
          {/* Mobile logo — editionsavhengig */}
          <div className="flex md:hidden items-center">
            <img
              src={isHousehold ? "https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/urzc6qg7_7540BF21-4413-4E40-BF72-4D12C742E009.png" : "https://customer-assets.emergentagent.com/job_recipe-shop-plan/artifacts/n0xjz0mz_E3443421-DE5D-4719-8957-3EE6E82E98C2.png"}
              alt="PantryBuddy"
              className={`w-auto rounded-lg ${isHousehold ? "h-12" : "h-10"}`}
              data-testid="header-logo"
            />
          </div>
          <div className="hidden md:block" />
          <div className="flex items-center gap-2 md:gap-3">
            {/* Edition switcher (dual-access users only) - hidden on mobile (moved to drawer) */}
            {canSwitchEdition && (
              <button
                onClick={switchEdition}
                className={`hidden md:inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border transition-colors ${isHousehold ? "bg-sailcloth-100 border-navy-200 text-navy-800 hover:bg-sailcloth-200" : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"}`}
                title={isHousehold ? "Bytt til Båt" : "Bytt til Home Edition"}
                data-testid="edition-switcher-btn"
              >
                {isHousehold ? <Anchor className="h-3.5 w-3.5" /> : <Home className="h-3.5 w-3.5" />}
                <span className="text-xs font-medium hidden sm:inline">
                  {isHousehold ? "Bytt til Båt" : "Bytt til Home"}
                </span>
                <Repeat className="h-3 w-3 opacity-60" />
              </button>
            )}
            {/* Boat switcher (kun for boat edition) / Household label (for home edition) — hidden on mobile (i drawer) */}
            {isHousehold ? (
              <div className="hidden md:flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1" data-testid="household-label">
                <Home className="h-3.5 w-3.5 text-amber-700" />
                <span className="text-xs font-medium text-amber-900 max-w-[140px] truncate">{effectiveBoat?.name || "Hjem"}</span>
              </div>
            ) : (
              <Popover open={boatMenuOpen} onOpenChange={setBoatMenuOpen}>
                <PopoverTrigger asChild>
                  <button className="hidden md:flex items-center gap-1.5 bg-sailcloth-100 border border-navy-100 rounded-lg px-2.5 py-1 hover:bg-navy-50 transition-colors" data-testid="boat-switcher-btn">
                    <Anchor className="h-3.5 w-3.5 text-seawater-600" />
                    <span className="text-xs font-medium text-navy-800 max-w-[120px] truncate">{activeBoat?.name || "Velg båt"}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 border-b border-stone-100">
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider px-2">Bytt båt</h3>
                  </div>
                  <div className="py-1">
                    {boatBoats.map((b) => (
                      <button key={b.id} onClick={() => switchBoat(b.id)} className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-50 transition-colors flex items-center gap-2 ${activeBoat?.id === b.id ? "bg-sailcloth-100 text-navy-800 font-medium" : "text-stone-700"}`} data-testid={`switch-boat-${b.id}`}>
                        <Ship className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{b.name}</span>
                        {activeBoat?.id === b.id && <Check className="h-3.5 w-3.5 ml-auto text-seawater-600" />}
                      </button>
                    ))}
                    {boatBoats.length === 0 && <p className="px-3 py-2 text-xs text-stone-400">Ingen båter ennå</p>}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {activeTrip && !isHousehold && (
              <div className="hidden md:flex items-center gap-1.5 bg-stone-50 border border-stone-200 rounded-lg px-2.5 py-1" data-testid="active-trip-badge">
                <Ship className="h-3.5 w-3.5 text-stone-500" />
                <span className="text-xs font-medium text-stone-600 max-w-[100px] truncate">{activeTrip.name}</span>
              </div>
            )}
            <Users className="h-4 w-4 text-stone-400 hidden md:block" />
            <span className="text-xs sm:text-sm font-medium text-stone-600 hidden md:block">Personer:</span>
            <Select value={String(servings)} onValueChange={(v) => setServings(Number(v))} data-testid="persons-selector">
              <SelectTrigger className="hidden md:flex w-16 md:w-20 h-8 md:h-9 border-stone-200 text-sm" data-testid="persons-selector-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent data-testid="persons-selector-content">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`persons-option-${n}`}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="h-6 w-px bg-stone-200 mx-0.5 hidden md:block" />
            {/* Theme toggle (Light / Dark / System) */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="hidden md:inline-flex p-1.5 md:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors"
                  title={`Tema: ${theme === "system" ? `System (${resolved})` : theme === "dark" ? "Mørk" : "Lys"}`}
                  data-testid="theme-toggle-btn"
                >
                  {resolved === "dark" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-1" align="end">
                {[
                  { v: "light", label: "Lyst", Icon: Sun },
                  { v: "dark", label: "Mørkt (nattskift)", Icon: Moon },
                  { v: "auto-night", label: "Auto natt 20–06", Icon: Clock },
                  { v: "system", label: "Følg system", Icon: Monitor },
                ].map(({ v, label, Icon }) => (
                  <button
                    key={v}
                    onClick={() => setTheme(v)}
                    className={`w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-stone-100 ${theme === v ? "bg-stone-100 text-navy-800 font-medium" : "text-stone-700"}`}
                    data-testid={`theme-option-${v}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                    {theme === v && <Check className="h-3.5 w-3.5 ml-auto text-seawater-600" />}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <div className="hidden md:block"><ExpiryNotifier /></div>
            {/* Offline-data popover (desktop) */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="hidden md:inline-flex p-1.5 md:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors"
                  title="Offline-data"
                  data-testid="offline-data-popover-trigger"
                >
                  <CloudDownload className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end">
                <OfflineDataCard />
              </PopoverContent>
            </Popover>
            {/* In-app notifications bell — Home Edition only */}
            {isHousehold && <NotificationBell />}
            {/* Notification bell (expiring pantry items) */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <button className="relative p-1.5 md:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" data-testid="notification-bell">
                  <Bell className="h-4 w-4" />
                  {notifications.total > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">{notifications.total}</span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-3 border-b border-stone-100">
                  <h3 className="text-sm font-semibold text-stone-800">Varsler</h3>
                </div>
                {notifications.total === 0 ? (
                  <div className="p-4 text-center text-sm text-stone-400">Ingen varsler</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto divide-y divide-stone-100">
                    {notifications.expired.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`notif-expired-${item.id}`}>
                        <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0"><AlertTriangle className="h-3.5 w-3.5 text-red-600" /></div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-red-700 block truncate">{item.name}</span>
                          <span className="text-xs text-red-500">Utgått {Math.abs(item.days_left)} dager siden</span>
                        </div>
                      </div>
                    ))}
                    {notifications.expiring.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 px-3 py-2.5" data-testid={`notif-expiring-${item.id}`}>
                        <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Clock className="h-3.5 w-3.5 text-amber-600" /></div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-amber-700 block truncate">{item.name}</span>
                          <span className="text-xs text-amber-500">{item.days_left === 0 ? "Utløper i dag!" : `Utløper om ${item.days_left} dag${item.days_left > 1 ? "er" : ""}`}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </PopoverContent>
            </Popover>
            <span className="text-xs sm:text-sm text-stone-500 hidden md:block" data-testid="user-name">{user?.name || user?.email || ""}</span>
            <button onClick={logout} className="hidden md:inline-flex p-1.5 md:p-2 hover:bg-stone-100 rounded-md text-stone-400 hover:text-stone-600 transition-colors" title="Logg ut" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </button>
            {/* Mobile hamburger — opens nav drawer */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md hover:bg-stone-100 text-stone-700"
              data-testid="mobile-menu-toggle"
              aria-label="Åpne meny"
              title="Meny"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </header>

        {/* Mobile Nav Drawer — alle sider + båt/skiftbytte + personer + logg ut */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0 flex flex-col" data-testid="mobile-menu-drawer">
            <SheetHeader className="px-4 py-3 border-b border-stone-200">
              <SheetTitle className="text-left text-base font-semibold text-stone-800" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Meny</SheetTitle>
              {/* Active boat / household context */}
              {isHousehold ? (
                <div className="flex items-center gap-2 mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                  <Home className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="truncate">{effectiveBoat?.name || "Hjem"}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-2 text-xs text-navy-800 bg-sailcloth-100 border border-navy-100 rounded-md px-2.5 py-1.5">
                  <Anchor className="h-3.5 w-3.5 flex-shrink-0 text-seawater-600" />
                  <span className="truncate">{activeBoat?.name || "Velg båt"}</span>
                  {activeTrip && <span className="ml-auto text-[10px] text-stone-500 truncate max-w-[80px]">{activeTrip.name}</span>}
                </div>
              )}
            </SheetHeader>
            <nav className="flex-1 overflow-y-auto py-2" data-testid="mobile-menu-nav">
              {visibleNavItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? "bg-stone-100 text-navy-800" : "text-stone-700 hover:bg-stone-50"
                    }`
                  }
                  data-testid={`mobile-menu-${item.shortLabel.toLowerCase()}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-stone-200 px-4 py-3 space-y-3 bg-stone-50/50">
              {/* Offline-data (last ned pantry + handleliste for offline-bruk) */}
              <OfflineDataCard compact />
              {/* Bytt edition */}
              {canSwitchEdition && (
                <button
                  onClick={() => { setMobileMenuOpen(false); switchEdition(); }}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 border text-sm font-medium transition-colors ${isHousehold ? "bg-sailcloth-100 border-navy-200 text-navy-800 hover:bg-sailcloth-200" : "bg-amber-50 border-amber-200 text-amber-900 hover:bg-amber-100"}`}
                  data-testid="mobile-edition-switch"
                >
                  {isHousehold ? <Anchor className="h-4 w-4" /> : <Home className="h-4 w-4" />}
                  {isHousehold ? "Bytt til Båt" : "Bytt til Home"}
                  <Repeat className="h-3 w-3 opacity-60" />
                </button>
              )}
              {/* Bytt båt — kun for boat-edition med fleire båtar */}
              {!isHousehold && boatBoats.length > 1 && (
                <div className="space-y-1">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">Bytt båt</p>
                  {boatBoats.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => switchBoat(b.id)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center gap-2 ${activeBoat?.id === b.id ? "bg-sailcloth-100 text-navy-800 font-medium" : "text-stone-700 hover:bg-stone-100"}`}
                      data-testid={`mobile-switch-boat-${b.id}`}
                    >
                      <Ship className="h-3.5 w-3.5 flex-shrink-0" />
                      <span className="truncate">{b.name}</span>
                      {activeBoat?.id === b.id && <Check className="h-3.5 w-3.5 ml-auto text-seawater-600" />}
                    </button>
                  ))}
                </div>
              )}
              {/* Personer */}
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-stone-400 flex-shrink-0" />
                <span className="text-sm text-stone-600">Personer:</span>
                <Select value={String(servings)} onValueChange={(v) => setServings(Number(v))}>
                  <SelectTrigger className="flex-1 h-9 border-stone-200 text-sm" data-testid="mobile-persons-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Tema */}
              <div className="grid grid-cols-2 gap-1">
                {[
                  { v: "light", label: "Lyst", Icon: Sun },
                  { v: "dark", label: "Mørkt", Icon: Moon },
                  { v: "auto-night", label: "Auto natt", Icon: Clock },
                  { v: "system", label: "System", Icon: Monitor },
                ].map(({ v, label, Icon }) => (
                  <button
                    key={v}
                    onClick={() => setTheme(v)}
                    className={`inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs border ${theme === v ? "bg-stone-100 text-navy-800 font-semibold border-stone-300" : "bg-white text-stone-600 border-stone-200"}`}
                    data-testid={`mobile-theme-${v}`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
              {/* User + logg ut */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-stone-200">
                <span className="text-xs text-stone-500 truncate flex-1" data-testid="mobile-user-name">{user?.name || user?.email || ""}</span>
                <button
                  onClick={() => { setMobileMenuOpen(false); logout(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-stone-200 text-stone-700 hover:bg-stone-100 text-sm"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="h-3.5 w-3.5" /> Logg ut
                </button>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto relative">
          <div className="relative z-10">
            <Outlet />
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
